import React, { useState } from "react";

import Icons from "../Icons/index";

const DatePicker = ({ onDateSelect, onClose, minDate = null }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Default minDate to tomorrow (start of day) if not provided
  // This prevents selecting today as a due date
  const effectiveMinDate =
    minDate ||
    (() => {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      return tomorrow;
    })();
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownType, setDropdownType] = useState(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = Array.from({ length: 10 }, (_, i) => currentYear - 3 + i);

  const getDaysInMonth = (month, year) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const generateDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    const prevMonthDays = getDaysInMonth(currentMonth - 1, currentYear);
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevMonthDays - i, isCurrentMonth: false });
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ day: i, isCurrentMonth: true });
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ day: i, isCurrentMonth: false });
    }

    return days;
  };

  // Check if a date is before the minimum allowed date
  const isDateDisabled = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    date.setHours(0, 0, 0, 0);
    return date < effectiveMinDate;
  };

  const handleDateSelect = (day) => {
    // Don't allow selecting disabled dates
    if (isDateDisabled(day)) return;

    const date = new Date(currentYear, currentMonth, day);
    setSelectedDate(date);
    onDateSelect?.(date);
  };

  const handleDropdownClick = (type) => {
    setDropdownType(type);
    setShowDropdown(true);
  };

  const handleSelect = (value) => {
    if (dropdownType === "month") {
      const monthIndex = months.indexOf(value);
      setCurrentMonth(monthIndex);
    } else if (dropdownType === "year") {
      setCurrentYear(parseInt(value));
    }
    setShowDropdown(false);
  };

  return (
    <div className="w-80 bg-[#1a2b4b] rounded-lg p-4 text-white pb-5 pt-5 relative">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => {
              if (currentMonth === 0) {
                setCurrentMonth(11);
                setCurrentYear((prev) => prev - 1);
              } else {
                setCurrentMonth((prev) => prev - 1);
              }
            }}
            className="p-1 hover:bg-slate-800 rounded"
          >
            <Icons type="leftChevron" className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleDropdownClick("month")}
            className="text-sm capitalize hover:bg-slate-800 px-2 py-1 rounded"
          >
            {months[currentMonth]}
          </button>
          <button
            type="button"
            onClick={() => {
              if (currentMonth === 11) {
                setCurrentMonth(0);
                setCurrentYear((prev) => prev + 1);
              } else {
                setCurrentMonth((prev) => prev + 1);
              }
            }}
            className="p-1 hover:bg-slate-800 rounded"
          >
            <Icons type="rightChevron" className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setCurrentYear((prev) => prev - 1);
            }}
            className="p-1 hover:bg-slate-800 rounded"
          >
            <Icons type="leftChevron" className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleDropdownClick("year")}
            className="text-sm hover:bg-slate-800 px-2 py-1 rounded"
          >
            {currentYear}
          </button>
          <button
            type="button"
            onClick={() => {
              setCurrentYear((prev) => prev + 1);
            }}
            className="p-1 hover:bg-slate-800 rounded"
          >
            <Icons type="rightChevron" className="w-4 h-4" />
          </button>
        </div>
      </div>

      {showDropdown && (
        <div className="absolute left-0 right-0 bg-[#1a2b4b] shadow-lg z-10 max-h-64 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:'none'] [scrollbar-width:'none']">
          {dropdownType === "month"
            ? months.map((month) => (
                <button
                  type="button"
                  key={month}
                  onClick={() => handleSelect(month)}
                  className="w-full text-left px-4 py-2 hover:bg-[#F4F6FB1F] focus:bg-slate-600"
                >
                  {month}
                </button>
              ))
            : years.map((year) => (
                <button
                  type="button"
                  key={year}
                  onClick={() => handleSelect(year.toString())}
                  className="w-full text-left px-4 py-2 hover:bg-[#F4F6FB1F] focus:bg-slate-600"
                >
                  {year}
                </button>
              ))}
        </div>
      )}

      <div className="grid grid-cols-7 gap-1 text-center text-sm mb-2">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <div key={`${day}-${index}`} className="p-1">
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 text-sm">
        {generateDays().map((day, index) => {
          // Only highlight if this is a current month day AND matches the selected date
          const isSelected =
            day.isCurrentMonth &&
            selectedDate &&
            selectedDate.getDate() === day.day &&
            selectedDate.getMonth() === currentMonth &&
            selectedDate.getFullYear() === currentYear;

          const isDisabled = day.isCurrentMonth && isDateDisabled(day.day);

          return (
            <button
              type="button"
              key={index}
              onClick={() =>
                day.isCurrentMonth && !isDisabled && handleDateSelect(day.day)
              }
              disabled={!day.isCurrentMonth || isDisabled}
              className={`p-1 rounded-full w-8 h-8 flex items-center justify-center ${
                !day.isCurrentMonth
                  ? "text-slate-600 cursor-default"
                  : isDisabled
                    ? "text-slate-600 cursor-not-allowed line-through"
                    : "hover:bg-persimmon cursor-pointer"
              } ${isSelected ? "bg-persimmon text-white" : ""}`}
            >
              {day.day}
            </button>
          );
        })}
      </div>

      <div className="mt-4 flex justify-between">
        <button
          type="button"
          onClick={() => {
            setSelectedDate(null);
            onDateSelect?.(null);
          }}
          className="px-4 py-1 text-sm text-red-500"
        >
          CLEAR
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1 text-sm text-red-500"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={() => onDateSelect?.(selectedDate)}
            className="px-4 py-1 text-sm text-persimmon"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
